import React, { Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Route, BrowserRouter as Router, Switch, Redirect } from "react-router-dom";
import "./index.css";
import AdminMessage from "./routes/AdminMessage/AdminMessage";
import FAQ from "./routes/FAQ/FAQ";
import Start from "./routes/Start/Start";
import Lobby from "./routes/Lobby/Lobby";
import OldLobby from "./routes/OldLobby/OldLobby";
import HowTo from "./routes/HowTo/HowTo";

import FeedbackPage from "./routes/FeedbackPage/FeedbackPage";
import FrontPage from "./routes/FrontPage/FrontPage";
import Counter from "./routes/Counter/Counter";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import DragAndDrop from "./routes/DragAndDrop/DragAndDrop";
import AlternateLobby from "./routes/Lobby/Lobby";
import { createTheme, ThemeProvider } from "@mui/material";
import { orange, red } from "@mui/material/colors";
import { colors } from "react-select/dist/declarations/src/theme";
import GameContextProvider from "./components/GameContextProvider/GameContextProvider";
import AnimatedBackground from "./components/AnimatedBackground/AnimatedBackground";
import { Auth0Provider, useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Auth0ProviderWithHistory from "./components/auth/auth0-provider-with-history";
import ProfilePage from "./routes/ProfilePage/ProfilePage";
import DeckEditorPage from "./routes/DeckEditorPage/DeckEditorPage";
import PrivateRoute from "./components/auth/PrivateRoute";
import SocketContextProvider from "./components/SocketContextProvider/SocketContextProvider";
import JoinPage from "./routes/JoinPage/JoinPage";
import AdminPanel from "./routes/AdminPanel/AdminPanel";
import AdminResultsPage from "./routes/AdminResultsPage/AdminResultsPage";
import DebugPage from "./routes/DebugPage/DebugPage";
import DebugGamePage from "./routes/DebugGamePage/DebugGamePage";
import ConfigPreviewPage from "./routes/ConfigPreviewPage/ConfigPreviewPage";
import ProfileIndicator from "./components/ProfileIndicator/ProfileIndicator";
import LeaderboardPage from "./routes/LeaderboardPage/LeaderboardPage";
import CreatorProfilePage from "./routes/CreatorProfilePage/CreatorProfilePage";
import DeckTutorial from "./routes/DeckTutorial/DeckTutorial";
import CommunityPage from "./routes/CommunityPage/CommunityPage";
import ShutdownDialog from "./components/ShutdownDialog/ShutdownDialog";
import AnalyzingPage from "./routes/AnalyzingPage/AnalyzingPage";
import SeoManager from "./components/SeoManager/SeoManager";
import { auth0Domain, auth0LoginOptions } from "./shared/runtime";

import translationEnBase from "./i18n/en";
import translationDeBase from "./i18n/de";
import translationFrBase from "./i18n/fr";
import translationEsBase from "./i18n/es";
import translationItBase from "./i18n/it";
import translationPtBase from "./i18n/pt";
import translationJpBase from "./i18n/jp";
import translationCnBase from "./i18n/cn";
import translationKrBase from "./i18n/kr";
import translationRuBase from "./i18n/ru";
import translationPlBase from "./i18n/pl";
import translationSeBase from "./i18n/se";
import translationDnBase from "./i18n/dn";
import translationNlBase from "./i18n/nl";
import translationPhBase from "./i18n/ph";
import translationInBase from "./i18n/in";
import translationSaBase from "./i18n/sa";
import translationIdBase from "./i18n/id";
import translationTrBase from "./i18n/tr";

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: translationEnBase },
    de: { translation: translationDeBase },
    fr: { translation: translationFrBase },
    es: { translation: translationEsBase },
    it: { translation: translationItBase },
    pt: { translation: translationPtBase },
    jp: { translation: translationJpBase },
    cn: { translation: translationCnBase },
    kr: { translation: translationKrBase },
    ru: { translation: translationRuBase },
    pl: { translation: translationPlBase },
    se: { translation: translationSeBase },
    dn: { translation: translationDnBase },
    nl: { translation: translationNlBase },
    ph: { translation: translationPhBase },
    in: { translation: translationInBase },
    sa: { translation: translationSaBase },
    id: { translation: translationIdBase },
    tr: { translation: translationTrBase },
  },
  lng: "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

const socketContext = React.createContext("counter");

const neutralColor: string = "#e8e5e0";

const theme = createTheme({
  status: {
    danger: orange[500],
    sus: red[100],
  },
  shape: {
    borderRadius: 10,
  },
  // Spacing in rem rather than px, so every gap and padding follows the root font size and thus
  // `--ui-scale` (see index.css). At the default 16px root this is exactly the MUI default —
  // theme.spacing(1) is still 8px — it only starts to differ on large displays.
  spacing: (factor: number) => `${0.5 * factor}rem`,
  palette: {
    primary: { main: "#D36667" },
    secondary: { main: "#ddd9d3" },
    neutral: { main: neutralColor },
    darkNeutral: { main: "#c4c0ba" },
    yellow: {main: "#fff355", secondary: "#FFF9AA"},
    blue: {main: "#608ad6", secondary:"#B0C5EB"},
    red: {main: "#e75151", secondary: "#F3A8A8" },
    green: {main: "#92dc67", secondary: "#C9EEB3" },
    orange: {main: "#e17d47", secondary: "#F0BEA3"},
    gray: {main: "#8c8c8c",secondary: "#8c8c8c"},
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: neutralColor,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: "#000",
        },
      },
    },
  },
});

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      danger: string;
      sus: string;
    };
    // breakpoints: {
    //   values: {
    //     xs: number;
    //     sm: number;
    //     md: number;
    //     lg: number;
    //     xl: number;
    //   }
    // }
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    status?: {
      danger?: string;
      sus?: string;
    };
    // breakpoints: {
    //   values: {
    //     xs: number;
    //     sm: number;
    //     md: number;
    //     lg: number;
    //     xl: number;
    //   }
    // }
  }
 
  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
    darkNeutral?: PaletteOptions["primary"];
    yellow?: PaletteOptions["primary"];
  
    blue?: PaletteOptions["primary"];
    red?:PaletteOptions["primary"];
    green?: PaletteOptions["primary"];
    orange?: PaletteOptions["primary"];
    gray?: PaletteOptions["primary"];
  }
  interface SimplePaletteColorOptions {
    // extra?: string;
    secondary?: string;
  }
}

const nativeLoginOptions = auth0LoginOptions(auth0Domain());
const ProtectedProfilePage = withAuthenticationRequired(ProfilePage, {
  loginOptions: nativeLoginOptions,
});
const ProtectedDeckEditorPage = withAuthenticationRequired(DeckEditorPage, {
  loginOptions: nativeLoginOptions,
});

function App() {
  // const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  // useEffect(() => {
  //   const upsertUser = async () => {
  //     if (isAuthenticated) {
  //       let token = await getAccessTokenSilently({ audience: "rank5.io" });
  //       //send Access token to api to create user if not exists
  //     }

  //   };
  //   upsertUser();
  // }, [isAuthenticated]);

  return (
    <Suspense fallback="Loading...">
      <ThemeProvider theme={theme}>
        <AnimatedBackground />
        <Router>
          <SeoManager />
          <GameContextProvider>
            <Auth0ProviderWithHistory>
              <ProfileIndicator />
              <ShutdownDialog />
              <Switch>
                <Route path="/play">
                  <Start />
                </Route>
                <Route path="/oldlobby">
                  <OldLobby />
                </Route>
                <Route path="/counter">
                  <Counter />
                </Route>
                <Route path="/faq">
                  <FAQ />
                </Route>
                <Route path="/howTo">
                  <HowTo />
                </Route>
                <Route path="/deck-tutorial">
                  <DeckTutorial />
                </Route>
                <Route path="/adminMessage">
                  <AdminMessage />
                </Route>
                <Route path="/feedback">
                  <FeedbackPage />
                </Route>
                <Route path="/lobby">
                  <SocketContextProvider>
                    <Lobby />
                  </SocketContextProvider>
                </Route>
                <Route path="/join">
                  <JoinPage/>
                </Route>
                <Route path="/config/:code">
                  <ConfigPreviewPage/>
                </Route>
                <Route path="/admin/results/:id">
                  <AdminResultsPage />
                </Route>
                <Route path="/admin">
                  <AdminPanel />
                </Route>
                {process.env.REACT_APP_DEVELOPMENT === "DEV" && (
                  <Route path="/debug_results">
                    <DebugPage />
                  </Route>
                )}
                {process.env.REACT_APP_DEVELOPMENT === "DEV" && (
                  <Route path="/debug_game">
                    <DebugGamePage />
                  </Route>
                )}
                <Route path="/analyzing">
                  <AnalyzingPage />
                </Route>
                <Route path="/community">
                  <CommunityPage />
                </Route>
                <Route path="/leaderboard">
                  <Redirect to="/community?tab=leaderboard" />
                </Route>
                <Route path="/creator/:name">
                  <CreatorProfilePage />
                </Route>
                <Route path="/deck/:id">
                  <ProtectedDeckEditorPage />
                </Route>
                <Route path="/profile">
                  <ProtectedProfilePage />
                </Route>
                <Route path="">
                  <FrontPage />
                </Route>
              </Switch>
            </Auth0ProviderWithHistory>
          </GameContextProvider>
        </Router>
      </ThemeProvider>
    </Suspense>
  );
}

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById("root")
);
